import { useParams, Link } from "react-router";
import { ArrowLeft, CheckCircle2, Mail, Phone, RotateCcw, Shield, XCircle } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Badge, Button, Card, CardContent, CardHeader, CardTitle } from "@bookdental/ui";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { ContaEAcesso } from "@/components/usuario/ContaEAcesso";
import { TransferirPropriedade } from "@/components/usuario/TransferirPropriedade";
import { SolicitacoesDoUsuario } from "@/components/lgpd/SolicitacoesDoUsuario";
import { VerComo } from "@/components/usuario/VerComo";

const CRO_STATUS_STYLES: Record<string, { label: string; className: string }> = {
  PENDING: { label: "Pendente", className: "bg-amber-100 text-amber-800" },
  VERIFIED: { label: "Verificado", className: "bg-green-100 text-green-800" },
  REJECTED: { label: "Rejeitado", className: "bg-red-100 text-red-800" },
};

function fmt(d: Date | null | undefined) {
  if (!d) return "—";
  return format(new Date(d), "dd MMM yyyy 'as' HH:mm", { locale: ptBR });
}

export function AdminUserDetail() {
  const { id } = useParams<{ id: string }>();
  const utils = trpc.useUtils();
  const { data: user, isLoading } = trpc.admin.users.getById.useQuery(
    { id: id! },
    { enabled: !!id },
  );

  const setRole = trpc.admin.users.setPlatformRole.useMutation({
    onSuccess: () => {
      toast.success("Role atualizado");
      utils.admin.users.getById.invalidate({ id: id! });
      utils.admin.users.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-admin-900 border-t-transparent" />
      </div>
    );
  }
  if (!user) return <div className="text-sm text-admin-700">Usuario nao encontrado.</div>;

  const croStatus = CRO_STATUS_STYLES[user.croStatus] ?? CRO_STATUS_STYLES.PENDING;
  const initial = (user.name || "?").charAt(0).toUpperCase();

  return (
    <div className="space-y-6">
      <Link
        to="/usuarios"
        className="inline-flex items-center gap-1.5 text-sm text-admin-700 transition-colors hover:text-admin-900"
      >
        <ArrowLeft className="h-4 w-4" />
        Voltar
      </Link>

      <Card className="border-admin-200">
        <CardContent className="flex items-start gap-5 p-6">
          {user.image ? (
            <img src={user.image} alt="" className="h-20 w-20 rounded-full object-cover" />
          ) : (
            <div className="flex h-20 w-20 items-center justify-center rounded-full bg-admin-900 text-2xl font-semibold text-white">
              {initial}
            </div>
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-bold text-admin-900">
                {user.name || "(sem nome)"}
              </h2>
              {user.platformRole === "ADMIN" && (
                <Badge className="bg-red-500 text-white hover:bg-red-500">
                  <Shield className="mr-1 h-3 w-3" />
                  ADMIN
                </Badge>
              )}
            </div>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-admin-700">
              <Mail className="h-3.5 w-3.5" />
              {user.email}
            </p>
            {user.phone && (
              <p className="mt-0.5 flex items-center gap-1.5 text-sm text-admin-700">
                <Phone className="h-3.5 w-3.5" />
                {user.phone}
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            {user.platformRole === "MEMBER" ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRole.mutate({ id: user.id, role: "ADMIN" })}
                disabled={setRole.isPending}
              >
                Tornar ADMIN
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRole.mutate({ id: user.id, role: "MEMBER" })}
                disabled={setRole.isPending}
              >
                Remover ADMIN
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <ContaEAcesso conta={user} />
        <TransferirPropriedade userId={user.id} vinculos={user.memberships} />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <SolicitacoesDoUsuario userId={user.id} />
        <VerComo userId={user.id} />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <Card className="border-admin-200">
          <CardHeader>
            <CardTitle>Registro</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <Row label="ID" value={user.id} mono />
            <Row label="Criado em" value={fmt(user.createdAt)} />
            <Row label="Ultimo acesso" value={fmt(user.lastLoginAt)} />
            <Row
              label="Onboarding"
              value={user.onboardingCompletedAt ? fmt(user.onboardingCompletedAt) : "Pendente"}
            />
          </CardContent>
        </Card>

        <Card className="border-admin-200">
          <CardHeader>
            <CardTitle>Profissional (CRO)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            {user.croNumber ? (
              <>
                <Row label="Numero" value={`CRO-${user.croState} ${user.croNumber}`} />
                <div className="grid grid-cols-[140px_1fr] items-center gap-3">
                  <span className="text-xs uppercase tracking-wider text-admin-700/60">Status</span>
                  <span
                    className={`inline-flex w-fit items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${croStatus.className}`}
                  >
                    {croStatus.label}
                  </span>
                </div>
                <CroActions userId={user.id} current={user.croStatus} />
              </>
            ) : (
              <p className="text-admin-700">Sem CRO cadastrado.</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card className="border-admin-200">
        <CardHeader>
          <CardTitle>Clinicas vinculadas ({user.memberships.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {user.memberships.length === 0 ? (
            <p className="text-sm text-admin-700">Nenhuma clinica.</p>
          ) : (
            <div className="space-y-4">
              {user.memberships.map((m: MembershipShape) => (
                <OrganizationCard key={m.organization.id} membership={m} />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

type MembershipShape = {
  role: string;
  organization: {
    id: string;
    name: string;
    slug: string;
    types: string[];
    radiologyProfile?: {
      services: string[];
      logoUrl?: string | null;
      addressSame: boolean;
      zipCode?: string | null;
      address?: string | null;
      addressNumber?: string | null;
      city?: string | null;
      state?: string | null;
    } | null;
    labProfile?: {
      name?: string | null;
      services: string[];
      logoUrl?: string | null;
      addressSame: boolean;
      zipCode?: string | null;
      address?: string | null;
      addressNumber?: string | null;
      city?: string | null;
      state?: string | null;
    } | null;
  };
};

function OrganizationCard({ membership: m }: { membership: MembershipShape }) {
  const { organization } = m;
  const hasRadio = organization.types.includes("RADIOLOGY_CENTER");
  const hasLab = organization.types.includes("DENTAL_LAB");

  return (
    <div className="rounded-lg border border-admin-100 bg-admin-50/30 p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <p className="text-sm font-semibold text-admin-900">{organization.name}</p>
          <p className="text-xs text-admin-700">
            {organization.slug} • {organization.types.join(", ")}
          </p>
        </div>
        <Badge variant="outline">{m.role}</Badge>
      </div>

      {hasRadio && organization.radiologyProfile && (
        <ProfileMini
          title="Radiologia"
          logoUrl={organization.radiologyProfile.logoUrl}
          services={organization.radiologyProfile.services}
          address={addressLine(organization.radiologyProfile)}
        />
      )}
      {hasLab && organization.labProfile && (
        <ProfileMini
          title={organization.labProfile.name || "Laboratorio"}
          logoUrl={organization.labProfile.logoUrl}
          services={organization.labProfile.services}
          address={addressLine(organization.labProfile)}
        />
      )}
      {(hasRadio && !organization.radiologyProfile) ||
      (hasLab && !organization.labProfile) ? (
        <p className="mt-2 text-[11px] italic text-admin-700/70">
          Alguns perfis ainda nao foram configurados.
        </p>
      ) : null}
    </div>
  );
}

function addressLine(p: {
  addressSame: boolean;
  zipCode?: string | null;
  address?: string | null;
  addressNumber?: string | null;
  city?: string | null;
  state?: string | null;
}) {
  if (p.addressSame) return "Mesmo endereco da clinica principal";
  const parts = [
    p.address,
    p.addressNumber,
    p.city && p.state ? `${p.city}/${p.state}` : null,
    p.zipCode,
  ].filter(Boolean);
  return parts.join(", ") || "Endereco nao preenchido";
}

function ProfileMini({
  title,
  logoUrl,
  services,
  address,
}: {
  title: string;
  logoUrl?: string | null;
  services: string[];
  address: string;
}) {
  return (
    <div className="mt-3 rounded-md border border-admin-100 bg-white p-3">
      <div className="flex items-start gap-3">
        {logoUrl ? (
          <img src={logoUrl} alt="" className="h-12 w-12 rounded-md border border-admin-100 object-contain" />
        ) : (
          <div className="h-12 w-12 rounded-md border border-dashed border-admin-200 bg-admin-50" />
        )}
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-admin-900">{title}</p>
          <p className="mt-0.5 text-[11px] text-admin-700">{address}</p>
          <div className="mt-1.5 flex flex-wrap gap-1">
            {services.length === 0 && (
              <span className="text-[11px] italic text-admin-700/70">Sem servicos configurados</span>
            )}
            {services.map((s) => (
              <span
                key={s}
                className="rounded-full bg-admin-100 px-2 py-0.5 text-[10px] font-medium text-admin-800"
              >
                {s}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="grid grid-cols-[140px_1fr] items-center gap-3">
      <span className="text-xs uppercase tracking-wider text-admin-700/60">{label}</span>
      <span className={mono ? "font-mono text-xs text-admin-800" : "text-admin-900"}>{value}</span>
    </div>
  );
}

function CroActions({
  userId,
  current,
}: {
  userId: string;
  current: "PENDING" | "VERIFIED" | "REJECTED";
}) {
  const utils = trpc.useUtils();
  const mutation = trpc.admin.users.setCroStatus.useMutation({
    onSuccess: (_data, vars) => {
      const labels = {
        VERIFIED: "CRO validado",
        REJECTED: "CRO rejeitado",
        PENDING: "Status resetado para pendente",
      } as const;
      toast.success(labels[vars.status]);
      utils.admin.users.getById.invalidate({ id: userId });
      utils.admin.users.list.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  return (
    <div className="flex flex-wrap gap-2 border-t border-admin-100 pt-3">
      <Button
        size="sm"
        variant={current === "VERIFIED" ? "default" : "outline"}
        onClick={() => mutation.mutate({ id: userId, status: "VERIFIED" })}
        disabled={mutation.isPending || current === "VERIFIED"}
        className={
          current === "VERIFIED"
            ? "bg-green-600 hover:bg-green-600"
            : "text-green-700 hover:bg-green-50 hover:text-green-800"
        }
      >
        <CheckCircle2 className="h-4 w-4" />
        {current === "VERIFIED" ? "Validado" : "Validar CRO"}
      </Button>
      <Button
        size="sm"
        variant="outline"
        onClick={() => mutation.mutate({ id: userId, status: "REJECTED" })}
        disabled={mutation.isPending || current === "REJECTED"}
        className="text-red-700 hover:bg-red-50 hover:text-red-800"
      >
        <XCircle className="h-4 w-4" />
        Rejeitar
      </Button>
      {current !== "PENDING" && (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => mutation.mutate({ id: userId, status: "PENDING" })}
          disabled={mutation.isPending}
          className="text-admin-700"
        >
          <RotateCcw className="h-4 w-4" />
          Reset
        </Button>
      )}
    </div>
  );
}
